import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import type { Card, Pack, PackProgress } from "../domain/types";
import { getCardsByPackAndLanguage, getPackById, getPackProgress } from "../db";
import { useLanguage } from "../state/language";
import { modeStyle } from "../components/modeTheme";
import { Button } from "../components/Button";

export default function PackEntry() {
  const { packId } = useParams<{ packId: string }>();
  const nav = useNavigate();
  const { lang, t } = useLanguage();
  const language = lang ?? "uk";
  const [pack, setPack] = useState<Pack | null>(null);
  const [progress, setProgress] = useState<PackProgress | null>(null);
  const [cards, setCards] = useState<Card[]>([]);

  useEffect(() => {
    if (!packId) return;
    (async () => {
      const p = (await getPackById(packId)) ?? null;
      setPack(p);
      const pr = (await getPackProgress(packId)) ?? null;
      setProgress(pr);
      const cs = await getCardsByPackAndLanguage(packId, language);
      setCards(cs);
    })();
  }, [packId, language]);

  // Phase distribution of MAIN cards — "emotional arc" preview that
  // replaces a flat number with the shape of the night ahead.
  const phaseCounts = useMemo(() => {
    const main = cards.filter((c) => (c.kind ?? "main") === "main");
    return {
      warmup: main.filter((c) => c.phase === "warmup").length,
      engagement: main.filter((c) => c.phase === "engagement").length,
      peak: main.filter((c) => c.phase === "peak").length,
    };
  }, [cards]);

  if (!packId) return null;
  if (!pack) {
    return <div className="pp-screen items-center justify-center">{t("loading")}</div>;
  }

  const status = progress?.status ?? "not_started";
  const title = pack.titleByLang[language] || pack.titleByLang.uk;
  const desc = pack.descByLang[language] || pack.descByLang.uk;

  let primaryLabel = t("cta_start");
  if (status === "in_progress") primaryLabel = t("cta_continue");
  if (status === "completed") primaryLabel = t("cta_play_again");

  // Status badge — colour matches mood: completed=warm green, in_progress=
  // amber forward-motion, new=accent pulse "fresh deck".
  const isNew = status === "not_started";
  const statusBadge =
    status === "completed"
      ? { label: t("pack_status_completed"), bg: "bg-emerald-400/20", border: "border-emerald-400/40", text: "text-emerald-100", pulse: false }
      : status === "in_progress"
      ? { label: t("pack_status_in_progress"), bg: "bg-amber-400/20", border: "border-amber-400/40", text: "text-amber-100", pulse: false }
      : { label: t("pack_status_not_started"), bg: "bg-[var(--accent,white)]/20", border: "border-[var(--accent,white)]/50", text: "text-white", pulse: true };

  const progressPct = progress?.progressPct ?? 0;
  const total = phaseCounts.warmup + phaseCounts.engagement + phaseCounts.peak || pack.totalCards;

  return (
    <div className="pp-screen px-5 relative" style={modeStyle(pack.modeId)}>
      <div className="pp-aurora" aria-hidden />
      <div className="pp-flare absolute top-0 left-0 right-0 h-72" />

      <header className="flex items-center pt-3 relative">
        <Button variant="ghost" size="sm" onClick={() => nav(-1)}>
          ← {t("cta_back")}
        </Button>
      </header>

      {/* HERO — title + bold manifesto-style description that sells the vibe */}
      <div className="mt-8 relative">
        <h1
          className="font-display text-4xl sm:text-5xl font-black tracking-tight leading-[1.05]"
          style={{ textShadow: "0 0 18px var(--accent, rgba(255,255,255,0.35))" }}
        >
          {title}
        </h1>
        <p className="mt-3 text-lg font-semibold opacity-95 leading-snug">{desc}</p>
      </div>

      {/* Status row — "fresh deck" energy via pulsing badge for new packs */}
      <div className="mt-6 relative flex items-center gap-2">
        <span
          className={`text-[11px] uppercase tracking-wider font-bold rounded-full px-3 py-1 border ${statusBadge.bg} ${statusBadge.border} ${statusBadge.text} ${statusBadge.pulse ? "pp-pulse-cta" : ""}`}
        >
          {isNew && "✨ "}{statusBadge.label}
        </span>
        {pack.isAdult && (
          <span className="text-[11px] uppercase tracking-wider rounded-full px-3 py-1 bg-black/40 border border-white/30">
            18+
          </span>
        )}
      </div>

      {/* Phase distribution — "emotional arc" preview, replaces the orphan
          big number that read as a workload counter. */}
      {total > 0 && (
        <div className="mt-8 relative">
          <div className="text-[10px] uppercase tracking-[0.2em] opacity-50 mb-2">
            {t("pack_arc_label")}
          </div>
          <div className="flex h-3 rounded-full overflow-hidden border border-white/10">
            <div
              className="transition-all"
              title={`Розігрів · ${phaseCounts.warmup}`}
              style={{
                width: `${(phaseCounts.warmup / total) * 100}%`,
                background: "linear-gradient(90deg, rgba(255,255,255,0.25), var(--accent, rgba(255,255,255,0.4)))",
              }}
            />
            <div
              className="transition-all"
              title={`Залучення · ${phaseCounts.engagement}`}
              style={{
                width: `${(phaseCounts.engagement / total) * 100}%`,
                background: "linear-gradient(90deg, var(--accent, rgba(255,255,255,0.5)), var(--accent, rgba(255,255,255,0.85)))",
                opacity: 0.85,
              }}
            />
            <div
              className="transition-all"
              title={`Пік · ${phaseCounts.peak}`}
              style={{
                width: `${(phaseCounts.peak / total) * 100}%`,
                background: "var(--accent, #fff)",
                boxShadow: "inset 0 0 12px rgba(255,255,255,0.4)",
              }}
            />
          </div>
          <div className="mt-2 flex justify-between text-[11px] opacity-80 tabular-nums">
            <span>🌅 {phaseCounts.warmup}</span>
            <span>🎉 {phaseCounts.engagement}</span>
            <span>🔥 {phaseCounts.peak}</span>
          </div>
        </div>
      )}

      {/* In-progress deep bar — appears only when continuing */}
      {status === "in_progress" && (
        <div className="mt-5 relative">
          <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
            <div
              className="h-full transition-all"
              style={{
                width: `${progressPct}%`,
                background: "var(--accent, #fff)",
                boxShadow: "0 0 12px var(--accent, rgba(255,255,255,0.6))",
              }}
            />
          </div>
          <div className="mt-1.5 text-[11px] opacity-60 tabular-nums">
            {progress?.cardsSeen ?? 0} / {pack.totalCards}
          </div>
        </div>
      )}

      {/* Action buttons — pulsing primary as invitation to enter the night */}
      <div className="mt-auto flex flex-col gap-3 pb-2 pt-12 relative">
        <button
          type="button"
          className="w-full px-6 py-5 rounded-2xl font-display font-black text-2xl uppercase tracking-wide bg-white text-black active:scale-95 transition pp-pulse-cta"
          style={{ boxShadow: "0 0 28px var(--accent, rgba(255,255,255,0.7)), 0 0 56px var(--accent, rgba(255,255,255,0.4))" }}
          onClick={() => nav(`/players/${pack.id}${status === "in_progress" ? "?resume=1" : ""}`)}
        >
          {primaryLabel} →
        </button>
        {status === "in_progress" && (
          <Button variant="outline" fullWidth onClick={() => nav(`/players/${pack.id}`)}>
            {t("cta_start_over")}
          </Button>
        )}
      </div>
    </div>
  );
}
